import type { Point } from './camera';
import type { OBB } from './geometry';
import type { PointerType } from './tools/tool';

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'rotate';
export const HANDLE_IDS: readonly HandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w', 'rotate'];
export const ROTATE_OFFSET_PX = 24;
const CORNERS_AND_ROTATE: readonly HandleId[] = ['nw', 'ne', 'se', 'sw', 'rotate'];
const NO_ROTATE: readonly HandleId[] = HANDLE_IDS.filter((h) => h !== 'rotate');
const WIDTH_ONLY: readonly HandleId[] = ['e', 'w'];

/**
 * The handles an object type offers: containers never rotate; images only scale from a corner (their ratio is fixed);
 * task lists only change width (their height follows their tasks, P16-5).
 */
export function handlesFor(type: string): readonly HandleId[] {
  return type === 'section' ? NO_ROTATE : type === 'image' ? CORNERS_AND_ROTATE : type === 'tasklist' ? WIDTH_ONLY : HANDLE_IDS;
}
/** Hit radius in screen px: 12px diameter for a mouse, 44px for a finger. */
export const HIT_RADIUS_PX: Record<PointerType, number> = { mouse: 6, pen: 10, touch: 22 };

const RAD = Math.PI / 180;

/** Handle position relative to the object centre, before rotation. Handle sizes are constant on screen (hence `zoom`). */
export function handleLocalPos(h: HandleId, w: number, hh: number, zoom: number): Point {
  const x = h.includes('e') ? w / 2 : h.includes('w') ? -w / 2 : 0;
  const y = h.includes('s') ? hh / 2 : h.includes('n') ? -hh / 2 : 0;
  if (h === 'rotate') return { x: 0, y: -hh / 2 - ROTATE_OFFSET_PX / zoom };
  return { x, y };
}

export function toWorld(o: OBB, p: Point): Point {
  const c = Math.cos(o.rotation * RAD);
  const s = Math.sin(o.rotation * RAD);
  return { x: o.cx + p.x * c - p.y * s, y: o.cy + p.x * s + p.y * c };
}

export function toLocal(o: OBB, p: Point): Point {
  const c = Math.cos(-o.rotation * RAD);
  const s = Math.sin(-o.rotation * RAD);
  const dx = p.x - o.cx;
  const dy = p.y - o.cy;
  return { x: dx * c - dy * s, y: dx * s + dy * c };
}

export function handleWorldPos(o: OBB, h: HandleId, zoom: number): Point {
  return toWorld(o, handleLocalPos(h, o.w, o.h, zoom));
}

/** Closest of `ids` within the pointer-type hit radius, or null. */
export function handleAt(o: OBB, p: Point, zoom: number, pointerType: PointerType, ids: readonly HandleId[] = HANDLE_IDS): HandleId | null {
  const radius = HIT_RADIUS_PX[pointerType] / zoom;
  let best: { id: HandleId; d: number } | null = null;
  for (const id of ids) {
    const hp = handleWorldPos(o, id, zoom);
    const d = Math.hypot(hp.x - p.x, hp.y - p.y);
    if (d <= radius && (!best || d < best.d)) best = { id, d };
  }
  return best?.id ?? null;
}

export type EdgeId = 'n' | 'e' | 's' | 'w';

/**
 * The side of the box under `p`: within the pointer-type hit radius outside the edge, and inside it by that radius but
 * never deeper than a quarter of the box (a small object keeps its middle to move), anywhere along it except the corner
 * zones (left to the corner handles). `dist` is the distance to that edge, in world units.
 */
export function edgeAt(o: OBB, p: Point, zoom: number, pointerType: PointerType): { edge: EdgeId; dist: number } | null {
  const r = HIT_RADIUS_PX[pointerType] / zoom;
  const { x, y } = toLocal(o, p);
  const hw = o.w / 2;
  const hh = o.h / 2;
  // [side, signed distance (outside > 0), how deep inside it may reach, whether the point is along it]
  const sides: [EdgeId, number, number, boolean][] = [
    ['n', -hh - y, Math.min(r, o.h / 4), Math.abs(x) <= hw - r],
    ['s', y - hh, Math.min(r, o.h / 4), Math.abs(x) <= hw - r],
    ['w', -hw - x, Math.min(r, o.w / 4), Math.abs(y) <= hh - r],
    ['e', x - hw, Math.min(r, o.w / 4), Math.abs(y) <= hh - r],
  ];
  let best: { edge: EdgeId; dist: number } | null = null;
  for (const [edge, out, inside, along] of sides) {
    const dist = Math.abs(out);
    if (along && out <= r && -out < inside && (!best || dist < best.dist)) best = { edge, dist };
  }
  return best;
}
